<template>
    <v-container fluid>
        <!-- ─── PDF Preview Overlay ─────────────────────────────────────────── -->
        <v-row v-if="showPdfPreview">
            <div class="pdf-overlay">
                <div class="pdf-container">
                    <v-btn
                        style="float: right; margin-top: 50px; margin-bottom: 10px"
                        depressed
                        icon
                        @click="closePdfPreview"
                    >
                        <v-icon large>fas fa-window-close</v-icon>
                    </v-btn>
                    <iframe id="pdf-preview-div" class="pdf-iframe" :src="pdfLink">
                        <p>Your browser does not support iframes.</p>
                    </iframe>
                </div>
            </div>
        </v-row>

        <!-- ─── Expenses Table ──────────────────────────────────────────────── -->
        <v-row>
            <v-col cols="12">
                <v-card flat outlined>
                    <v-card-title>
                        {{ tableTitle }}
                        <v-btn v-if="inEditMode" class="ml-2 mr-2" color="primary" @click="openAddDialog">
                            <v-icon small>fa-plus</v-icon>&nbsp;{{ $t('buttons.add') }}
                        </v-btn>
                    </v-card-title>

                    <v-card-text>
                        <v-data-table
                            :headers="objectHeaders"
                            :items="objectList"
                            fixed-header
                            dense
                            class="row-item"
                            :items-per-page="-1"
                            :hide-default-footer="true"
                            :height="200"
                            :no-data-text="$t('labels.noData')"
                        >
                            <template v-slot:item="{item}">
                                <v-tooltip bottom>
                                    <template v-slot:activator="{on, attrs}">
                                        <tr class="cursor-pointer" v-on="on" v-bind="attrs">
                                            <!-- Invoice / AG Fee -->
                                            <td>
                                                <CheckBox
                                                    :readonly="true"
                                                    :value="isSeafarerType ? item.invoice : item.ag_fee"
                                                />
                                            </td>

                                            <!-- Pay Slip -->
                                            <td>
                                                <CheckBox :readonly="true" v-model="item.pay_slip" />
                                            </td>

                                            <!-- Clickable data cells -->
                                            <td @click="editExpense(item)">{{ item.name_display }}</td>
                                            <td @click="editExpense(item)">{{ item.date_display }}</td>
                                            <td class="text-right" @click="editExpense(item)">{{ item.value }}</td>
                                            <td @click="editExpense(item)">{{ item.currency_display }}</td>
                                            <td @click="editExpense(item)">{{ item.target_currency_display }}</td>
                                            <td @click="editExpense(item)">{{ item.exchange_rate }}</td>
                                            <td @click="editExpense(item)">{{ item.target_value }}</td>

                                            <!-- Uploaded files -->
                                            <td>
                                                <v-row
                                                    dense
                                                    v-for="file in item.uploaded_files"
                                                    :key="file.original_name"
                                                >
                                                    <v-col>
                                                        <v-btn @click="previewFile(file)" depressed icon>
                                                            <v-icon small>fas fa-eye</v-icon>
                                                        </v-btn>
                                                        {{ file.original_name }}
                                                    </v-col>
                                                </v-row>
                                            </td>

                                            <!-- Status dropdown -->
                                            <td style="width: 10%">
                                                <Dropdown
                                                    :readonly="true"
                                                    v-model="item.expense_status_id"
                                                    :ajax="true"
                                                    :data="statusDropdownData(item)"
                                                />
                                            </td>

                                            <!-- Action icons -->
                                            <td class="text-center">
                                                <!-- Return to pending -->
                                                <v-tooltip bottom v-if="item.expense_status_id === STATUS.APPROVED">
                                                    <template v-slot:activator="{on, attrs}">
                                                        <v-icon
                                                            color="#FFCF27"
                                                            class="pl-2"
                                                            v-bind="attrs"
                                                            v-on="on"
                                                            medium
                                                            @click="changeStatus(item, STATUS.PENDING)"
                                                        >
                                                            fas fa-pause
                                                        </v-icon>
                                                    </template>
                                                    <span>{{ $t('tooltips.returnToPending') }}</span>
                                                </v-tooltip>

                                                <!-- Approve -->
                                                <v-tooltip bottom v-if="item.expense_status_id === STATUS.PENDING">
                                                    <template v-slot:activator="{on, attrs}">
                                                        <v-icon
                                                            color="#0000DC"
                                                            class="pl-2"
                                                            v-bind="attrs"
                                                            v-on="on"
                                                            medium
                                                            @click="changeStatus(item, STATUS.APPROVED)"
                                                        >
                                                            fas fa-thumbs-up
                                                        </v-icon>
                                                    </template>
                                                    <span>{{ $t('tooltips.approveExpense') }}</span>
                                                </v-tooltip>

                                                <!-- Reject -->
                                                <v-tooltip bottom v-if="isActionable(item)">
                                                    <template v-slot:activator="{on, attrs}">
                                                        <v-icon
                                                            color="#FE0000"
                                                            class="pl-2"
                                                            v-bind="attrs"
                                                            v-on="on"
                                                            medium
                                                            @click="changeStatus(item, STATUS.REJECTED)"
                                                        >
                                                            fas fa-exclamation-triangle
                                                        </v-icon>
                                                    </template>
                                                    <span>{{ $t('tooltips.rejectExpense') }}</span>
                                                </v-tooltip>

                                                <!-- Send to billing -->
                                                <v-tooltip bottom v-if="isActionable(item)">
                                                    <template v-slot:activator="{on, attrs}">
                                                        <v-icon
                                                            color="#157811"
                                                            class="pl-2"
                                                            v-bind="attrs"
                                                            v-on="on"
                                                            medium
                                                            @click="changeStatus(item, STATUS.BILLED)"
                                                        >
                                                            fas fa-coins
                                                        </v-icon>
                                                    </template>
                                                    <span>{{ $t('tooltips.sendToBilling') }}</span>
                                                </v-tooltip>
                                            </td>

                                            <!-- Log -->
                                            <td>{{ item.log_display }}</td>
                                        </tr>
                                    </template>

                                    <span v-if="item.expense_status_id === STATUS.PENDING && inEditMode">
                                        {{ $t('tooltips.edit') }}
                                    </span>
                                </v-tooltip>
                            </template>
                        </v-data-table>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- ─── Add / Edit Dialog ────────────────────────────────────────────── -->
        <v-dialog persistent v-model="itemEditDialog" width="600">
            <v-card>
                <v-card-title>
                    {{ object.id ? $t('labels.editExpense') : $t('labels.newExpense') }}
                </v-card-title>

                <v-card-text>
                    <v-row>
                        <v-col cols="12">
                            <v-row dense>
                                <!-- Invoice / AG Fee -->
                                <v-col cols="12" class="ml-1">
                                    <CheckBox
                                        v-if="isSeafarerType"
                                        :readonly="!inEditMode"
                                        v-model="object.invoice"
                                        :label="$t('fields.invoice')"
                                    />
                                    <CheckBox
                                        v-else
                                        :readonly="agFeeReadOnly"
                                        v-model="object.ag_fee"
                                        :label="$t('fields.agFee')"
                                    />
                                </v-col>

                                <!-- Pay Slip -->
                                <v-col cols="12" class="ml-1">
                                    <CheckBox v-model="object.pay_slip" :label="$t('fields.payslip')" />
                                </v-col>

                                <!-- Name -->
                                <v-col cols="12">
                                    <InputField
                                        :maxlength="isSeafarerType ? 300 : 150"
                                        v-model="object.name"
                                        :errorBorder="!objectValidation.name"
                                        :label="$t('fields.name')"
                                    />
                                </v-col>

                                <!-- Date -->
                                <v-col cols="12">
                                    <DatePicker
                                        v-model="object.date"
                                        :errorBorder="!objectValidation.date"
                                        :date-label="$t('fields.date')"
                                    />
                                </v-col>

                                <!-- Value -->
                                <v-col cols="12">
                                    <CurrencyField
                                        :max="100000"
                                        :min="isSeafarerType ? -100000 : 0.01"
                                        v-model="object.value"
                                        :errorBorder="!objectValidation.value"
                                        :label="$t('fields.value')"
                                    />
                                </v-col>

                                <!-- Source currency -->
                                <v-col cols="12">
                                    <Dropdown
                                        v-model="object.currency_id"
                                        :errorBorder="!objectValidation.currency_id"
                                        :ajax="true"
                                        :data="'currencies'"
                                        :label="$t('fields.currency')"
                                    />
                                </v-col>

                                <!-- Target currency (read-only, driven by placement) -->
                                <v-col cols="12">
                                    <Dropdown
                                        :readonly="true"
                                        v-model="object.target_currency_id"
                                        :ajax="true"
                                        :data="'currencies'"
                                        :label="$t('fields.targetCurrency')"
                                        :key="componentKey"
                                    />
                                </v-col>

                                <!-- Exchange rate -->
                                <v-col cols="12">
                                    <NumberField
                                        :max="100000"
                                        v-model="object.exchange_rate"
                                        :precision="5"
                                        :errorBorder="!objectValidation.exchange_rate"
                                        :label="$t('fields.exchangeRate')"
                                    />
                                </v-col>

                                <!-- Target value (computed, read-only) -->
                                <v-col cols="12">
                                    <CurrencyField
                                        :readonly="true"
                                        v-model="object.target_value"
                                        :precision="2"
                                        :label="$t('fields.targetValue')"
                                    />
                                </v-col>

                                <!-- File upload -->
                                <v-col cols="12">
                                    <MultipleUpload
                                        :accept="'pdf'"
                                        v-model="object.uploaded_files"
                                        :errorBorder="!objectValidation.uploaded_files"
                                        :label="$t('fields.files')"
                                    />
                                </v-col>
                            </v-row>

                            <!-- Dialog actions -->
                            <v-row>
                                <v-col
                                    cols="12"
                                    md="6"
                                    class="text-right"
                                    :class="{'text-center': $vuetify.breakpoint.smAndDown}"
                                >
                                    <v-btn color="primary" class="white--text" @click.stop="submitExpense">
                                        {{ $t('buttons.save') }}
                                    </v-btn>
                                </v-col>
                                <v-col
                                    cols="12"
                                    md="6"
                                    class="text-left"
                                    :class="{'text-center': $vuetify.breakpoint.smAndDown}"
                                >
                                    <v-btn color="secondary" class="white--text" @click.stop="cancelEditExpense">
                                        {{ $t('buttons.cancel') }}
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import Dropdown from './trinkets/Dropdown';
import CheckBox from './trinkets/CheckBox';
import InputField from './trinkets/InputField';
import Expense from '../models/Expense';
import DatePicker from './trinkets/DatePicker.vue';
import CurrencyField from '@/components/trinkets/CurrencyField';
import NumberField from './trinkets/NumberField.vue';
import MultipleUpload from './trinkets/MultipleUpload.vue';

// ─── Constants ────────────────────────────────────────────────────────────────

// Expense status IDs — keeps magic numbers out of template and business logic
const STATUS = Object.freeze({
    PENDING: 1,
    APPROVED: 2,
    REJECTED: 3,
    BILLED: 4,
    PAID: 5,
});

// Maps each target status to its confirmation alert i18n key
const STATUS_ALERT_KEY = {
    [STATUS.PENDING]: 'returnExpenseToPendingQuestion',
    [STATUS.APPROVED]: 'approveExpenseQuestion',
    [STATUS.REJECTED]: 'rejectExpenseQuestion',
    [STATUS.BILLED]: 'sendToBillingExpenseQuestion',
};

// Only PDF files are accepted
const VALID_FILE_EXTENSIONS = new Set(['pdf', 'PDF']);

// ─── Factories ────────────────────────────────────────────────────────────────

function makeBlankExpense() {
    return new Expense({});
}
function makeBlankValidation() {
    return new Expense({}, true);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Return true if the file's extension is in the allowed set.
 * An empty extension (file still being named) is treated as valid so we don't
 * reject uploads mid-flight.
 */
function isValidFile(file) {
    const ext = file.original_name.split('.').pop();
    return VALID_FILE_EXTENSIONS.has(ext) || ext === '';
}

// ─── Component ────────────────────────────────────────────────────────────────

export default {
    name: 'PlacementExpensesTable',

    components: {InputField, CheckBox, Dropdown, DatePicker, CurrencyField, NumberField, MultipleUpload},

    props: {
        placement: {required: true},
        inEditMode: {type: Boolean, default: false},
        /** 1 = seafarer expense, 2 = company expense */
        type: {type: Number, required: true},
        agFeeReadOnly: {type: Boolean, default: false},
        targetCurrency: {required: true},
    },

    data() {
        return {
            STATUS,

            // Form model and its validation state
            object: makeBlankExpense(),
            objectValidation: makeBlankValidation(),

            // Table data
            objectList: [],
            objectHeaders: [],

            // Supporting data fetched once on mount
            exchangeRates: [],
            currencies: [],

            // PDF preview
            showPdfPreview: false,
            pdfLink: '',

            // Dialog visibility
            itemEditDialog: false,

            // Forces the target-currency dropdown to re-render after a reset
            componentKey: 1,
        };
    },

    computed: {
        isSeafarerType() {
            return this.type === 1;
        },
        tableTitle() {
            return this.isSeafarerType ? this.$t('labels.seafarerExpenses') : this.$t('labels.companyExpenses');
        },
    },

    watch: {
        // Recalculate whenever the manually-editable rate changes
        'object.exchange_rate'() {
            this.calculateTargetValue();
        },

        // Fetch fresh rates whenever the user picks a new date
        'object.date'(date) {
            if (!date) return;
            this.$APICall('get', 'exchange-rates', {date}, (data, success) => {
                if (success) {
                    this.exchangeRates = JSON.parse(JSON.stringify(data.exchange_rate));
                    this.handleExchangeRate();
                } else {
                    this.$alert(this.$t('alerts.basicError'));
                }
            });
        },

        // Re-look up the rate when the source currency changes
        'object.currency_id'() {
            this.handleExchangeRate();
            this.calculateTargetValue();
        },

        // Recalculate when the raw value changes
        'object.value'() {
            this.calculateTargetValue();
        },

        /**
         * Strip any non-PDF files as soon as they are added.
         *
         * BUG FIX: original code called array.splice() inside forEach(), mutating
         * the array while iterating over it — causing items to be skipped.
         * Fixed by building a new filtered array instead.
         */
        'object.uploaded_files'(files) {
            const invalid = files.filter((f) => !isValidFile(f));
            if (invalid.length) {
                this.object.uploaded_files = files.filter(isValidFile);
                this.$alert(this.$t('alerts.onlyPdf'));
            }
        },
    },

    methods: {
        // ── UI helpers ──────────────────────────────────────────────────────

        /** Pending and Approved expenses can still be acted on */
        isActionable(item) {
            return item.expense_status_id === STATUS.PENDING || item.expense_status_id === STATUS.APPROVED;
        },

        /**
         * Billed/Paid expenses use a read-only dropdown list; others allow
         * in-place status changes via a narrower editable list.
         */
        statusDropdownData(item) {
            return item.expense_status_id === STATUS.BILLED || item.expense_status_id === STATUS.PAID
                ? 'expense-statuses'
                : 'expense-statuses-edit';
        },

        // ── Dialog lifecycle ────────────────────────────────────────────────

        openAddDialog() {
            this.resetForm();
            this.itemEditDialog = true;
            this.componentKey++; // forces target-currency dropdown to re-render
        },

        cancelEditExpense() {
            this.itemEditDialog = false;
            this.resetForm();
        },

        resetForm() {
            this.object = makeBlankExpense();
            this.objectValidation = makeBlankValidation();
            this.object.target_currency_id = this.targetCurrency;
        },

        // ── PDF preview ─────────────────────────────────────────────────────

        previewFile(file) {
            this.pdfLink = file.public_link;
            this.showPdfPreview = true;
        },

        closePdfPreview() {
            this.showPdfPreview = false;
            this.pdfLink = '';
        },

        // ── Validation ──────────────────────────────────────────────────────

        /**
         * Validate the current form object.
         * Populates objectValidation with per-field boolean flags and
         * returns true only when all required fields are present and files are valid.
         */
        validateExpense() {
            const o = this.object;
            let valid = true;

            // File type check
            if (!o.uploaded_files.every(isValidFile)) {
                this.$alert(this.$t('alerts.onlyPdf'));
                valid = false;
            }

            // Required fields
            const requiredFields = ['name', 'date', 'value', 'currency_id', 'exchange_rate', 'target_value'];
            requiredFields.forEach((field) => {
                const ok = o[field] != null && o[field] !== '';
                this.objectValidation[field] = ok;
                if (!ok) valid = false;
            });

            return valid;
        },

        // ── CRUD ────────────────────────────────────────────────────────────

        /** Single save entry point — branches on whether we're adding or editing */
        submitExpense() {
            if (this.object.id) {
                this.saveExpenseChanges();
            } else {
                this.addExpense();
            }
        },

        addExpense() {
            if (!this.validateExpense()) return;

            const expense = {
                ...this.object,
                placement_id: this.placement.id,
                expense_status_id: STATUS.PENDING,
                user_id: this.$myStore.state.user.id,
                expense_type_id: this.type,
            };

            this.$APICall('post', 'expenses', {expense}, (data, success) => {
                if (success) {
                    this.cancelEditExpense();
                    this.reloadData();
                } else {
                    this.$notify(this.$t('alerts.basicError'));
                }
            });
        },

        saveExpenseChanges() {
            if (!this.validateExpense()) return;

            const expense = {
                ...this.object,
                expense_status_id: STATUS.PENDING,
                user_id: this.$myStore.state.user.id,
            };

            this.$APICall('put', 'expenses/' + expense.id, {expense}, (data, success) => {
                if (success) {
                    this.cancelEditExpense();
                    this.reloadData();
                } else {
                    this.$notify(this.$t('alerts.basicError'));
                }
            });
        },

        /** Only editable when status is Pending and the parent is in edit mode */
        editExpense(item) {
            if (item.expense_status_id !== STATUS.PENDING || !this.inEditMode) return;

            this.$APICall('get', 'expenses/' + item.id, {}, (data, success) => {
                if (success) {
                    this.object = data.expense;
                    this.objectValidation = makeBlankValidation();
                    this.itemEditDialog = true;
                } else {
                    this.$notify(this.$t('alerts.basicError'));
                }
            });
        },

        changeStatus(item, newStatus) {
            const alertKey = STATUS_ALERT_KEY[newStatus];
            this.$verify(this.$t('alerts.' + alertKey), (result) => {
                if (!result) return;

                const expense = {
                    ...item,
                    expense_status_id: newStatus,
                    user_id: this.$myStore.state.user.id,
                };

                this.$APICall('put', 'expenses-status/' + expense.id, {expense}, (data, success) => {
                    if (success) {
                        this.$notify(this.$t('alerts.basicUpdate'));
                        this.reloadData();
                    } else {
                        this.$notify(this.$t('alerts.basicError'));
                    }
                });
            });
        },

        // ── Exchange rate logic ─────────────────────────────────────────────

        /**
         * Compute target_value from value, currency_id, and target_currency_id.
         *
         * All rates from the API are expressed as: 1 EUR = N units of that currency.
         * EUR (id=1) has no entry in the table — its implicit rate is always 1.
         *
         * Conversions:
         *   same currency        :  target = value
         *   EUR   → foreign      :  target = value × targetRate
         *                           e.g. 100 EUR × 1.1628 = 116.28 USD
         *   foreign → EUR        :  target = value ÷ sourceRate
         *                           e.g. 100 USD ÷ 1.1628 = 86.00 EUR
         *   foreign → foreign    :  target = (value ÷ sourceRate) × targetRate
         *                           e.g. 100 USD → EUR → GBP
         */
        calculateTargetValue() {
            const {currency_id, target_currency_id, value} = this.object;

            if (currency_id == null || target_currency_id == null || value == null) return;

            const BASE_ID = 1; // EUR
            const val = parseFloat(value);

            const getRate = (id) => {
                if (id === BASE_ID) return 1;
                const entry = this.exchangeRates.find((r) => r.currency_id === id);
                return entry ? parseFloat(entry.rate) : null;
            };

            let result;

            if (currency_id === target_currency_id) {
                result = val;
            } else if (currency_id === BASE_ID) {
                // EUR → foreign
                const targetRate = getRate(target_currency_id);
                if (!targetRate) {
                    this.object.target_value = 0;
                    return;
                }
                result = val * targetRate;
            } else if (target_currency_id === BASE_ID) {
                // foreign → EUR
                const sourceRate = getRate(currency_id);
                if (!sourceRate) {
                    this.object.target_value = 0;
                    return;
                }
                result = val / sourceRate;
            } else {
                // foreign → foreign (via EUR)
                const sourceRate = getRate(currency_id);
                const targetRate = getRate(target_currency_id);
                if (!sourceRate || !targetRate) {
                    this.object.target_value = 0;
                    return;
                }
                result = (val / sourceRate) * targetRate;
            }

            this.object.target_value = parseFloat(result.toFixed(2));
        },

        /**
         * Set exchange_rate on the form when currency or date changes.
         * This is the rate shown to the user for reference; the actual conversion
         * in calculateTargetValue always reads directly from this.exchangeRates
         * so it is never stale.
         *
         * For the displayed rate we show: 1 EUR = N source-currency units
         * (i.e. the source currency's entry from the ECB table).
         * When source is EUR, rate is 1. When source is unknown, rate is 0.
         */
        handleExchangeRate() {
            const {currency_id, target_currency_id} = this.object;

            if (currency_id === target_currency_id) {
                this.object.exchange_rate = 1;
                return;
            }

            if (currency_id === 1) {
                // Source is EUR — show the target currency's rate (1 EUR = N target)
                const entry = this.exchangeRates.find((r) => r.currency_id === target_currency_id);
                this.object.exchange_rate = entry ? parseFloat(entry.rate) : 0;
            } else {
                // Source is foreign — show 1 EUR = N source units
                const entry = this.exchangeRates.find((r) => r.currency_id === currency_id);
                this.object.exchange_rate = entry ? parseFloat(entry.rate) : 0;
            }
        },

        // ── Data loading ────────────────────────────────────────────────────

        reloadData() {
            this.$APICall('get', 'placement-expenses/' + this.placement.id, {phase: this.type}, (data, success) => {
                if (success) this.objectList = data.items;
            });
        },

        buildHeaders() {
            const firstCol = this.isSeafarerType
                ? {text: this.$t('fields.inv'), sortable: false, align: 'center'}
                : {text: this.$t('fields.agFee'), sortable: false};

            const commonCols = [
                {text: this.$t('fields.paySlip'), sortable: false},
                {text: this.$t('fields.name'), sortable: false},
                {text: this.$t('fields.date'), sortable: false},
                {text: this.$t('fields.value'), sortable: false, align: 'right'},
                {text: this.$t('fields.currency'), sortable: false},
                {text: this.$t('fields.targetCurrency'), sortable: false},
                {text: this.$t('fields.exchangeRate'), sortable: false},
                {text: this.$t('fields.targetValue'), sortable: false},
                {text: this.$t('fields.files'), sortable: false},
                {text: this.$t('fields.status'), sortable: false, align: 'center', width: '300px'},
                {text: this.$t('fields.actions'), sortable: false, align: 'center', width: '300px'},
                {text: this.$t('fields.log'), sortable: false},
            ];

            return [firstCol, ...commonCols];
        },
    },

    mounted() {
        this.objectHeaders = this.buildHeaders();
        this.object.target_currency_id = this.targetCurrency;
        this.reloadData();
    },
};
</script>

<style scoped>
.pdf-overlay {
    display: block;
    z-index: 9999999;
    position: fixed;
    top: 0;
    left: 0;
    background-color: rgba(255, 255, 255, 0.074);
    width: 100%;
    height: 100vw;
}

.pdf-container {
    width: 40%;
    height: 100vh;
    margin: 0 auto;
}

.pdf-iframe {
    width: 100%;
    margin-bottom: 20px;
    height: 87vh;
    display: block;
}

.cursor-pointer {
    cursor: pointer;
}
</style>
